import { CSSProperties, useEffect, useState, ReactNode } from 'react';
import useBrand from 'Hooks/useBrand';
import { connectToBrand_api } from 'api/brandApi';
import { clsx, showToast, styx } from "modules/Utils"
import Intercom from "helpers/Intercom";
import Colors from 'modules/Colors';
import CardSymphony from 'elements/CardSymphony';
import Icon from 'elements/Icon';
import ButtonSymphony from 'elements/ButtonSymphony';
import ModalSymphony from 'elements/ModalSymphony';
import InputSymphony from 'elements/InputSymphony';
import TagSymphony from 'elements/TagSymphony';
import FeatureGate from 'components/FeatureGate';
import { FeatureKeys } from 'features/definitions';
import Sty from 'modules/Sty';

const isValidFacebookPixel = (str: string) => {
    return (/^[0-9]{1,16}$/i).test(str.toString());
}

const isValidTikTokPixel = (str: string) => {
    return (/^[A-Z0-9]{20}$/i).test(str.toString());
}

const isValidGA4Id = (str: string) => {
    return (/^G-[A-Z0-9]{10}$/i).test(str.toString());
}

interface ConversionsStepProps {
    className?: string;
    title: string;
    description: string;
    children: ReactNode;
    isCompleted: boolean;
    isSuccessful: boolean;
    live?: boolean;
    showIntercomButton?: boolean;
    onClickIntercomButton?: () => void;
}

const renderConversionsStep = ({
    className = '',
    title,
    description,
    children,
    isCompleted,
    isSuccessful,
    live,
    showIntercomButton,
    onClickIntercomButton,
}: ConversionsStepProps) => {
    const renderHeader = () => {
        return (
            <div
                className="headerContainerConversionsConfigStep"
                style={sty.stepHeader}
            >
                <div style={sty.stepNumber}>
                    {isCompleted ? (
                        <Icon
                            className="checkedIcon"
                            icon="check"
                            size={16}
                            color="white"
                        />
                    ) : (
                        <div style={sty.stepNumberText}></div>
                    )}
                </div>
                <div style={sty.stepTitle}>
                    {title}
                </div>
                {live && (
                    <TagSymphony
                        className="liveTag"
                        color="green"
                        title="LIVE"
                    />
                )}
            </div>
        );
    };

    return (
        <CardSymphony
            className={clsx('conversionsConfigStepComponent', className)}
            style={sty.conversionsCard}
            isPadding16
        >
            <div>
                {renderHeader()}
                <div style={sty.stepBody}>
                    <div style={sty.stepDescription}>
                        {description}
                    </div>
                    {showIntercomButton && onClickIntercomButton && (
                        <ButtonSymphony
                            className="watchVideoButton"
                            onClick={onClickIntercomButton}
                            variant="text"
                        >
                            Need help? Click here
                        </ButtonSymphony>
                    )}
                    {children}
                </div>
            </div>
        </CardSymphony>
    );
};

const FacebookPixelSectionV2 = () => {
    const {
        currentBrand,
        isProBrand,
        getBrand
    } = useBrand();

    const {
        website_facebook_pixels,
        facebook_pixel,
        facebook_pixel_conversions_api_access_token,
    } = currentBrand?.connections || {};

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [pixelInputs, setPixelInputs] = useState<string[]>(['']);
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<boolean>(false);

    const hasPixelsButNotPro = Boolean(website_facebook_pixels?.length) && !isProBrand;
    const adsPixelId = facebook_pixel?.id;
    const adsPixelAlreadyInWebsitePixels = adsPixelId && website_facebook_pixels?.includes(adsPixelId);

    const handleOpenModal = () => {
        const currentPixels = website_facebook_pixels || [];
        
        // Initialize modal with current pixels or empty array
        if (currentPixels.length > 0) {
            setPixelInputs(currentPixels);
        } else {
            setPixelInputs(['']);
        }
        
        // Auto-include ads pixel if it exists and isn't already in website pixels
        if (adsPixelId && !adsPixelAlreadyInWebsitePixels && isProBrand) {
            const hasEmptySlot = pixelInputs.some(pixel => !pixel.trim());
            
            if (hasEmptySlot) {
                const newPixels = [...pixelInputs];
                const emptyIndex = newPixels.findIndex(pixel => !pixel.trim());
                newPixels[emptyIndex] = adsPixelId;
                setPixelInputs(newPixels);
            } else {
                setPixelInputs([adsPixelId, ...pixelInputs]);
            }
        }
        
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setError(false);
    };

    const addNewPixelInput = () => {
        setPixelInputs([...pixelInputs, '']);
    };

    const removePixelInput = (index: number) => {
        const newInputs = pixelInputs.filter((_, i) => i !== index);
        setPixelInputs(newInputs.length === 0 ? [''] : newInputs);
    };

    const updatePixelInput = (index: number, value: string) => {
        const newInputs = [...pixelInputs];
        newInputs[index] = value;
        setPixelInputs(newInputs);
    };

    const handleSave = async () => {
        setLoading(true);

        const validPixels = pixelInputs.filter(pixel => pixel.trim().length > 0 && isValidFacebookPixel(pixel.trim()));

        const response = await connectToBrand_api({
            brandId: Number(currentBrand?.id),
            brandSlug: String(currentBrand?.slug),
            value: validPixels,
            service: 'website_facebook_pixels'
        });

        if (response) {
            await getBrand();
            setError(false);
            showToast({ error: false, message: `${validPixels.length} Website Pixel${validPixels.length === 1 ? '' : 's'} connected successfully` });
            handleCloseModal();
        } else {
            showToast({ error: true, message: 'Failed to connect your Website Pixels' });
            setError(true);
        }
        setLoading(false);
    };

    const isAnyInputValid = pixelInputs.some(pixel => pixel.trim().length > 0 && isValidFacebookPixel(pixel.trim()));
    const isLastInputEmpty = pixelInputs[pixelInputs.length - 1]?.trim() === '';

    let description = 'Add Facebook Pixel IDs for website tracking.';
    if (adsPixelId) {
        description += ' Your ad campaign pixel is automatically included for unified tracking.';
    }

    if (hasPixelsButNotPro) {
        description = 'Your Website Pixels are connected but inactive. Upgrade to Pro to activate tracking.';
    }

    const renderCurrentPixels = () => {
        if (!website_facebook_pixels?.length && !adsPixelId) {
            return (
                <div style={sty.noConnectionsMessage}>
                    No Facebook Pixels connected
                </div>
            );
        }

        const allPixels = [...(website_facebook_pixels || [])];
        if (adsPixelId && !website_facebook_pixels?.includes(adsPixelId)) {
            allPixels.unshift(adsPixelId);
        }

        return (
            <div style={sty.connectedItemsList}>
                {allPixels.map((pixelId, index) => {
                    const isAdsPixel = pixelId === adsPixelId;
                    return (
                        <div key={index} style={sty.connectedItem}>
                            <Icon
                                className="facebookPixelIcon"
                                icon="facebook"
                                size={20}
                            />
                            <div style={sty.connectedItemContent}>
                                <div style={sty.connectedItemTitle}>
                                    {isAdsPixel ? `Campaign Pixel: ${pixelId}` : `Pixel ID: ${pixelId}`}
                                </div>
                                {isAdsPixel && facebook_pixel_conversions_api_access_token && (
                                    <div style={sty.conversionsApiEnabled}>
                                        Conversions API enabled
                                    </div>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>
        );
    };

    const renderModal = () => (
        <ModalSymphony
            className="facebookPixelsModal"
            isOpen={isModalOpen}
            onClose={handleCloseModal}
            title="Facebook Pixels (Website Tracking)"
            subtitle="Add Facebook Pixel IDs for website tracking"
            size="medium"
            buttonLeftText="Cancel"
            buttonLeftVariant="text"
            buttonLeftDisabled={loading}
            buttonRightText={hasPixelsButNotPro ? 'Upgrade to activate' : 'Save Pixels'}
            buttonRightVariant="outlined"
            onClickButtonLeft={handleCloseModal}
            onClickButtonRight={handleSave}
            buttonRightIsLoading={loading}
            buttonRightDisabled={loading || !isAnyInputValid}
        >
            <div style={sty.modalContent}>
                <div style={sty.modalDescription}>
                    {description}
                </div>

                <div style={sty.pixelInputsList}>
                    {pixelInputs.map((pixelValue, index) => {
                        const isAdsPixel = pixelValue === adsPixelId;
                        const hasError = pixelValue.length > 0 && !isValidFacebookPixel(pixelValue);
                        
                        return (
                            <div key={index} style={sty.pixelInputRow}>
                                <div style={sty.pixelInputContainer}>
                                    <InputSymphony
                                        className={`facebookPixelInput-${index}`}
                                        label={isAdsPixel ? 'Campaign Pixel (Auto-included)' : `Pixel ID ${index + 1}`}
                                        value={pixelValue}
                                        onChange={(value: string) => updatePixelInput(index, value)}
                                        placeholder={isAdsPixel ? `Campaign Pixel: ${pixelValue}` : '1234567890123456'}
                                        disabled={!isProBrand || isAdsPixel}
                                        error={hasError ? 'Invalid Facebook Pixel ID format' : undefined}
                                        helperText={hasError ? 'Invalid Facebook Pixel ID format' : undefined}
                                    />
                                </div>
                                {!isAdsPixel && pixelInputs.length > 1 && (
                                    <ButtonSymphony
                                        className="removePixelButton"
                                        variant="text"
                                        size="small"
                                        onClick={() => removePixelInput(index)}
                                        style={sty.removeButton}
                                    >
                                        <Icon className="trashIcon" icon="trash" size={16} />
                                    </ButtonSymphony>
                                )}
                            </div>
                        );
                    })}
                </div>

                <ButtonSymphony
                    className="addPixelButton"
                    variant="outlined"
                    size="small"
                    onClick={addNewPixelInput}
                    disabled={!isProBrand || isLastInputEmpty}
                    style={sty.addButton}
                >
                    Add Another Pixel
                </ButtonSymphony>

                <div style={sty.modalHelpText} onClick={() => Intercom.openWhereIsMyFacebookPixelArticle()}>
                    Need help finding your Facebook Pixel ID? Click here to learn how.
                </div>
            </div>
        </ModalSymphony>
    );

    return (
        <>
            {renderConversionsStep({
                className: 'facebookPixelSectionComponent',
                title: 'Facebook Pixels (Website Tracking)',
                description,
                isCompleted: Boolean(website_facebook_pixels?.length),
                isSuccessful: !error,
                live: Boolean(website_facebook_pixels?.length) && isProBrand,
                showIntercomButton: true,
                onClickIntercomButton: Intercom.openWhereIsMyFacebookPixelArticle,
                children: (
                    <div style={sty.stepContent}>
                        {renderCurrentPixels()}
                        <ButtonSymphony
                            className="editFacebookPixelsButton"
                            variant="outlined"
                            size="small"
                            onClick={handleOpenModal}
                            disabled={!isProBrand && !website_facebook_pixels?.length}
                        >
                            {website_facebook_pixels?.length ? 'Edit Pixels' : 'Connect Pixels'}
                        </ButtonSymphony>
                    </div>
                )
            })}
            {renderModal()}
        </>
    );
};

const TikTokSection = () => {
    const {
        currentBrand,
        isProBrand,
        getBrand
    } = useBrand();

    const {
        tiktok_pixel
    } = currentBrand?.connections || {};

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [tiktokValue, setTiktokValue] = useState<string>('');
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<boolean>(false);

    const hasPixelButNotPro = Boolean(tiktok_pixel) && !isProBrand;

    const handleOpenModal = () => {
        setTiktokValue(tiktok_pixel || '');
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setError(false);
    };

    const handleSave = async () => {
        setLoading(true);
        const response = await connectToBrand_api({
            brandId: Number(currentBrand?.id),
            brandSlug: String(currentBrand?.slug),
            value: tiktokValue,
            service: 'tiktok_pixel'
        });
        
        if (response) {
            await getBrand();
            setError(false);
            showToast({ error: false, message: 'TikTok Pixel connected successfully' });
            handleCloseModal();
        } else {
            showToast({ error: true, message: 'Failed to connect your TikTok Pixel' });
            setError(true);
        }
        setLoading(false);
    };

    const description = hasPixelButNotPro
        ? 'Your TikTok Pixel is connected but inactive. Upgrade to Pro to activate tracking.'
        : 'Add a TikTok Pixel you want to use with Symphony.';

    const renderCurrentPixel = () => {
        if (!tiktok_pixel) {
            return (
                <div style={sty.noConnectionsMessage}>
                    No TikTok Pixel connected
                </div>
            );
        }

        return (
            <div style={sty.connectedItemsList}>
                <div style={sty.connectedItem}>
                    <Icon
                        className="tiktokPixelIcon"
                        icon="tiktok"
                        size={20}
                    />
                    <div style={sty.connectedItemContent}>
                        <div style={sty.connectedItemTitle}>
                            Pixel ID: {tiktok_pixel}
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    const renderModal = () => (
        <ModalSymphony
            className="tiktokPixelModal"
            isOpen={isModalOpen}
            onClose={handleCloseModal}
            title="TikTok Pixel"
            subtitle="Add a TikTok Pixel you want to use with Symphony"
            size="medium"
            buttonLeftText="Cancel"
            buttonLeftVariant="text"
            buttonLeftDisabled={loading}
            buttonRightText={hasPixelButNotPro ? 'Upgrade to activate' : (Boolean(tiktok_pixel) ? 'Update' : 'Save')}
            buttonRightVariant="outlined"
            onClickButtonLeft={handleCloseModal}
            onClickButtonRight={handleSave}
            buttonRightIsLoading={loading}
            buttonRightDisabled={loading || !isValidTikTokPixel(tiktokValue)}
        >
            <div style={sty.modalContent}>
                <div style={sty.modalDescription}>
                    {description}
                </div>

                <InputSymphony
                    className="tiktokPixelInput"
                    label="TikTok Pixel ID"
                    value={tiktokValue}
                    onChange={(value: string) => setTiktokValue(value)}
                    placeholder="ABC1234567890000000"
                    disabled={!isProBrand}
                    error={tiktokValue.length > 0 && !isValidTikTokPixel(tiktokValue) ? 'Invalid TikTok Pixel ID format' : undefined}
                    helperText={tiktokValue.length > 0 && !isValidTikTokPixel(tiktokValue) ? 'Invalid TikTok Pixel ID format' : undefined}
                />

                <div style={sty.modalHelpText} onClick={() => Intercom.openWhereIsMyTiktokPixelArticle()}>
                    Need help finding your TikTok Pixel ID? Click here to learn how.
                </div>
            </div>
        </ModalSymphony>
    );

    return (
        <>
            {renderConversionsStep({
                className: 'tikTokSectionComponent',
                title: 'TikTok Pixel',
                description,
                isCompleted: Boolean(tiktok_pixel),
                isSuccessful: !error,
                live: Boolean(tiktok_pixel) && isProBrand,
                showIntercomButton: true,
                onClickIntercomButton: Intercom.openWhereIsMyTiktokPixelArticle,
                children: (
                    <div style={sty.stepContent}>
                        {renderCurrentPixel()}
                        <ButtonSymphony
                            className="editTikTokPixelButton"
                            variant="outlined"
                            size="small"
                            onClick={handleOpenModal}
                            disabled={!isProBrand && !tiktok_pixel}
                        >
                            {tiktok_pixel ? 'Edit Pixel' : 'Connect Pixel'}
                        </ButtonSymphony>
                    </div>
                )
            })}
            {renderModal()}
        </>
    );
};

const GoogleAnalyticsSection = () => {
    const {
        currentBrand,
        isProBrand,
        getBrand
    } = useBrand();

    const {
        google_analytics
    } = currentBrand?.connections || {};

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [googleAnalyticsValue, setGoogleAnalyticsValue] = useState<string>('');
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<boolean>(false);

    const hasTokenButNotPro = Boolean(google_analytics) && !isProBrand;

    const handleOpenModal = () => {
        setGoogleAnalyticsValue(google_analytics || '');
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setError(false);
    };

    const handleSave = async () => {
        setLoading(true);
        const response = await connectToBrand_api({
            brandId: Number(currentBrand?.id),
            brandSlug: String(currentBrand?.slug),
            value: googleAnalyticsValue,
            service: 'google_analytics'
        });
        
        if (response) {
            await getBrand();
            showToast({ error: false, message: 'Google Analytics Token connected successfully' });
            setError(false);
            handleCloseModal();
        } else {
            showToast({ error: true, message: 'Failed to connect your Google Analytics Token' });
            setError(true);
        }
        setLoading(false);
    };

    const description = hasTokenButNotPro
        ? 'Your Google Analytics is connected but inactive. Upgrade to Pro to activate tracking.'
        : 'Add a Google Analytics Token you want to use with Symphony.';

    const renderCurrentToken = () => {
        if (!google_analytics) {
            return (
                <div style={sty.noConnectionsMessage}>
                    No Google Analytics Token connected
                </div>
            );
        }

        return (
            <div style={sty.connectedItemsList}>
                <div style={sty.connectedItem}>
                    <Icon
                        className="googleAnalyticsIcon"
                        icon="google"
                        size={20}
                    />
                    <div style={sty.connectedItemContent}>
                        <div style={sty.connectedItemTitle}>
                            Token ID: {google_analytics}
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    const renderModal = () => (
        <ModalSymphony
            className="googleAnalyticsModal"
            isOpen={isModalOpen}
            onClose={handleCloseModal}
            title="Google Analytics Token"
            subtitle="Add a Google Analytics Token you want to use with Symphony"
            size="medium"
            buttonLeftText="Cancel"
            buttonLeftVariant="text"
            buttonLeftDisabled={loading}
            buttonRightText={hasTokenButNotPro ? 'Upgrade to activate' : (Boolean(google_analytics) ? 'Update' : 'Save')}
            buttonRightVariant="outlined"
            onClickButtonLeft={handleCloseModal}
            onClickButtonRight={handleSave}
            buttonRightIsLoading={loading}
            buttonRightDisabled={loading || !isValidGA4Id(googleAnalyticsValue)}
        >
            <div style={sty.modalContent}>
                <div style={sty.modalDescription}>
                    {description}
                </div>

                <InputSymphony
                    className="googleAnalyticsInput"
                    label="Google Analytics GA4 ID"
                    value={googleAnalyticsValue}
                    onChange={(value: string) => setGoogleAnalyticsValue(value)}
                    placeholder="G-XXXXXXXXXX"
                    disabled={!isProBrand}
                    error={googleAnalyticsValue.length > 0 && !isValidGA4Id(googleAnalyticsValue) ? 'Invalid Google Analytics GA4 ID format' : undefined}
                    helperText={googleAnalyticsValue.length > 0 && !isValidGA4Id(googleAnalyticsValue) ? 'Invalid Google Analytics GA4 ID format' : undefined}
                />

                <div style={sty.modalHelpText} onClick={() => Intercom.openWhereIsMyGoogleAnalyticsArticle()}>
                    Need help finding your Google Analytics GA4 ID? Click here to learn how.
                </div>
            </div>
        </ModalSymphony>
    );

    return (
        <>
            {renderConversionsStep({
                className: 'googleAnalyticsSectionComponent',
                title: 'Google Analytics Token',
                description,
                isCompleted: Boolean(google_analytics),
                isSuccessful: !error,
                live: Boolean(google_analytics) && isProBrand,
                showIntercomButton: true,
                onClickIntercomButton: Intercom.openWhereIsMyGoogleAnalyticsArticle,
                children: (
                    <div style={sty.stepContent}>
                        {renderCurrentToken()}
                        <ButtonSymphony
                            className="editGoogleAnalyticsButton"
                            variant="outlined"
                            size="small"
                            onClick={handleOpenModal}
                            disabled={!isProBrand && !google_analytics}
                        >
                            {google_analytics ? 'Edit Token' : 'Connect Token'}
                        </ButtonSymphony>
                    </div>
                )
            })}
            {renderModal()}
        </>
    );
};

interface ConnectSectionProps {
    className: string;
    style?: CSSProperties;
    /** Custom title for the connection banner */
    bannerTitle?: string;
    /** Custom subtitle for the connection banner */
    bannerSubtitle?: string;
    /** Custom upgrade source for tracking */
    upgradeSource?: string;
    /** Custom upgrade call to action text */
    upgradeCallToAction?: string;
}

const ConnectSection = ({
    className,
    style,
    bannerTitle,
    bannerSubtitle,
    upgradeSource,
    upgradeCallToAction,
}: ConnectSectionProps) => {
    const showConnectionDisclaimer = () => {
        return (
            <FeatureGate
                feature={FeatureKeys.WEBSITE_PIXEL_TRACKING}
                upgradeSource={upgradeSource || "Website Editor - Connect Section"}
                upgradeCallToAction={upgradeCallToAction || "Track your website + unlock advanced analytics with Symphony Growth"}
                showBanner={true}
            >
                <CardSymphony
                    className="connectSectionBanner"
                    title={bannerTitle || "Heads up: your connections are added to all of your websites."}
                    subtitle={bannerSubtitle || "Updating them here will add them to every published site."}
                    size="small"
                    style={{ backgroundColor: Colors.purpleLighter }}
                />
            </FeatureGate>
        )
    }

    return (
        <div
            className={clsx('connectSectionComponent', className)}
            style={styx(sty.connectSectionComponent, style)}
        >
            {showConnectionDisclaimer()}
            <TikTokSection />
            <GoogleAnalyticsSection />
            <FacebookPixelSectionV2 />
        </div>
    );
};

const sty: Record<string, CSSProperties> = {
    connectSectionComponent: {
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        gap: 16,
    },
    iconContainer: {
        width: 24,
        height: 24,
        borderRadius: 8,
        padding: 4,
        backgroundColor: Colors.VIDEO_BUTTON_CONTAINER,
    },
    // ConversionsStep styles
    conversionsCard: {
        width: '100%',
    },
    stepHeader: {
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        marginBottom: 16,
    },
    stepNumber: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        width: 24,
        height: 24,
        borderRadius: '50%',
        backgroundColor: Colors.green,
    },
    stepNumberText: {
        ...Sty.text14bold,
        color: Colors.purple,
    },

    stepTitle: {
        ...Sty.text16bold,
        color: Colors.black,
    },
    stepBody: {
        display: 'flex',
        flexDirection: 'column',
        gap: 16,
    },
    stepDescription: {
        ...Sty.text16grey,
    },
    stepContent: {
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
    },
    // Connection display styles
    noConnectionsMessage: {
        ...Sty.text16grey,
        fontStyle: 'italic',
    },
    connectedItemsList: {
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
    },
    connectedItem: {
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: 12,
        backgroundColor: Colors.greyLighter,
        borderRadius: 8,
    },
    connectedItemContent: {
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
    },
    connectedItemTitle: {
        ...Sty.text16,
        color: Colors.black,
    },
    conversionsApiEnabled: {
        ...Sty.text12,
        color: Colors.green,
    },
    // Modal styles
    modalContent: {
        display: 'flex',
        flexDirection: 'column',
        gap: 16,
    },
    modalDescription: {
        ...Sty.text16,
        color: Colors.black,
    },
    modalHelpText: {
        ...Sty.text12grey,
        cursor: 'pointer',
        textDecoration: 'underline',
        color: Colors.purpleLight,
    },
    // Facebook Pixels specific modal styles
    pixelInputsList: {
        display: 'flex',
        flexDirection: 'column',
        gap: 16,
    },
    pixelInputRow: {
        display: 'flex',
        alignItems: 'flex-start',
        gap: 8,
    },
    pixelInputContainer: {
        flex: 1,
    },
    removeButton: {
        minWidth: 'auto',
        padding: 8,
        color: Colors.red,
        marginTop: 8,
    },
    addButton: {
        alignSelf: 'flex-start',
    },
}

export default ConnectSection;